import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { ConfirmationNumber as ConfirmationNumberIcon } from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import MasterDetailLayout from '../../../shared/components/MasterDetailLayout';
import { useViewport } from '../../../shared/hooks/useViewport';
import type { EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import { useTicketList } from '../hooks/useTicketList';
import TicketDetailPanel from './TicketDetailPanel';
import { safeFormatDate } from '../../../shared';
import type { TicketView } from '../../../types/backend';

const listDate = (value?: string) =>
  safeFormatDate(value, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }, '');

const ticketListConfig: EntityListItemConfig<TicketView> = {
  getId: (t) => t.id,
  getPrimaryText: (t) => t.subject || `Ticket ${t.id}`,
  getSecondaryText: (t) =>
    [[t.status, t.priority].filter(Boolean).join(' · '), listDate(t.updated_at ?? t.created_at)]
      .filter(Boolean)
      .join('  ·  ') || '—',
  getSearchText: (t) => [t.id, t.subject, t.status, t.priority].filter(Boolean).join(' '),
  secondaryTypographyProps: { variant: 'caption' },
};

interface TicketManagementModalProps {
  customerId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

const TicketManagementModal: React.FC<TicketManagementModalProps> = ({ customerId, isOpen, onClose }) => {
  const { isCompact } = useViewport();
  const { tickets, loading, error } = useTicketList(isOpen ? customerId : undefined);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
    }
  }, [isOpen]);

  const selectedTicket = tickets.find((t) => t.id === selectedId) ?? null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isCompact}
      PaperProps={{ sx: isCompact ? undefined : { height: '90vh', maxHeight: '90vh' } }}
    >
      <ModalHeader icon={ConfirmationNumberIcon} title="Tickets" onClose={onClose} />

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        <MasterDetailLayout
          hasSelection={selectedId !== null}
          onBack={() => setSelectedId(null)}
          listWidth={340}
          backLabel="Back to tickets"
          list={
            <EntityListPanel
              entities={tickets}
              selectedEntity={selectedTicket}
              isNew={false}
              saving={false}
              config={ticketListConfig}
              emptyStateText="No tickets found"
              emptyStateHelperText="This customer has no ITSM tickets"
              onSelect={(t) => setSelectedId(t.id)}
              isLoading={loading}
              searchable
              // Below `md` the detail pane is hidden until a ticket is picked, so a
              // failed list load is said where the reader is looking: above the list.
              headerContent={isCompact && error ? <InlineAlert severity="error">{error}</InlineAlert> : undefined}
            />
          }
          detail={error ? (
            <InlineAlert severity="error" sx={{ m: 3 }}>
              {error}
            </InlineAlert>
          ) : (
            <TicketDetailPanel customerId={customerId} ticketId={selectedId} />
          )}
        />
      </DialogContent>

      <ModalFooter
        count={tickets.length}
        label={`ticket${tickets.length !== 1 ? 's' : ''}`}
        onClose={onClose}
        py={1.5}
        buttonSize="small"
      />
    </Dialog>
  );
};

export default TicketManagementModal;
